// When a leg's match is, in words a punter reads at a glance.
//
// A bare "7:20 pm" is only unambiguous on the day it is printed. The pad rolls
// to tomorrow's board in the evening (`showingTomorrow` in store.js), so a
// leg's kickoff needs to say WHICH day it is, not just what time — "7:20 pm"
// when it is today, "Tomorrow 7:20 pm" when the pad has rolled, and the day
// name further out. Once a leg's kickoff has passed, none of that matters any
// more: it just started.
//
// Calendar days are compared as formatted YYYY-MM-DD strings in the device
// zone (or `tz` for tests), never by adding 86,400,000ms — a day is not always
// 24 hours where a DST transition falls inside it, and the ms arithmetic
// quietly gets that wrong twice a year.

import { time, dayShort } from './format.js';
// `localDate` is tickets.js's, not reimplemented here: tickets.js imports
// nothing itself, so pulling in this one function does not drag in the engine.
import { localDate } from './tickets.js';

/**
 * YYYY-MM-DD plus one calendar day. String arithmetic on the Y/M/D parts, the
 * same shape as `addDay` in store.js — adding 24 hours in milliseconds and
 * re-reading the calendar day lands on the wrong day either side of a
 * daylight-saving change, because the day either side of the change is not 24
 * hours long. This is pure calendar arithmetic on the parts, so it never looks
 * at an offset at all.
 */
function nextDay(date) {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * A leg's kickoff, in milliseconds since the epoch, or null if it cannot be
 * read. A placed leg carries `commenceTime` (the field the store freezes off
 * the provider); a proposal's leg carries `kickoff` (see `lib/proposals.js`).
 * The one definition, shared with `resultOverdue`'s caller in results-due.js.
 */
export const legKickoff = (leg) => leg?.commenceTime ?? leg?.kickoff ?? null;

/**
 * The pieces of a when-label: `state` for anyone who wants to style or test
 * the case separately, `day`/`time` for anyone who wants them apart, and
 * `text` — the one thing everything else in this file exists to build.
 *
 * `state` is one of:
 *   'started' — kickoff is at or before `now`
 *   'today'   — kickoff falls on `now`'s local calendar day
 *   'tomorrow'— kickoff falls on the very next local calendar day
 *   'later'   — anything further out
 *   null      — `kickoff` is missing or unparseable; `text` is ''
 */
export function whenParts(kickoff, now = Date.now(), { tz } = {}) {
  const at = kickoff instanceof Date
    ? kickoff.getTime()
    : typeof kickoff === 'number' ? kickoff : Date.parse(kickoff);
  if (!Number.isFinite(at)) return { state: null, day: '', time: '', text: '' };

  if (at <= now) return { state: 'started', day: '', time: '', text: 'started' };

  const kickoffDay = localDate(at, tz);
  const todayDay = localDate(now, tz);
  const tomorrowDay = nextDay(todayDay);

  const clock = time(new Date(at).toISOString(), tz);

  if (kickoffDay === todayDay) return { state: 'today', day: '', time: clock, text: clock };
  if (kickoffDay === tomorrowDay) {
    return { state: 'tomorrow', day: 'Tomorrow', time: clock, text: `Tomorrow ${clock}` };
  }
  const day = dayShort(new Date(at).toISOString(), tz);
  return { state: 'later', day, time: clock, text: `${day} ${clock}` };
}

/** Just the text a caller prints — see `whenParts`. */
export function whenLabel(kickoff, now = Date.now(), opts) {
  return whenParts(kickoff, now, opts).text;
}
